import { ensureSupabaseConfigured, supabase, User } from '../lib/supabase';

export interface AuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isAdmin: boolean;
  userStatusPreference: 'consumer' | 'vendor' | 'applicant' | 'member';
}

export class AuthService {
  // Sign up a new user
  static async signUp(
    email: string, 
    password: string, 
    firstName?: string, 
    lastName?: string, 
    isAdmin: boolean = false,
    userStatusPreference: 'consumer' | 'vendor' | 'applicant' | 'member' = 'consumer',
    alias?: string
  ) {
    try {
      ensureSupabaseConfigured();
      const cleanAlias = (alias || '').trim() || undefined;

      if (cleanAlias) {
        const { data: existing, error: aliasErr } = await supabase
          .from('users')
          .select('id')
          .ilike('alias', cleanAlias)
          .limit(1);

        if (aliasErr) throw aliasErr;
        if (Array.isArray(existing) && existing.length > 0) {
          throw new Error('This alias already exists. Please choose a different one.');
        }
      }

      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('No user data returned');

      // Create user record in users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .insert({
          id: authData.user.id,
          email,
          first_name: firstName || null,
          last_name: lastName || null,
          is_admin: isAdmin,
          user_status_preference: userStatusPreference,
          alias: cleanAlias || null,
        })
        .select()
        .single();

      if (userError) {
        const msg = String((userError as any)?.message || '');
        if (msg.includes('users_alias_key') || msg.toLowerCase().includes('duplicate key')) {
          throw new Error('This alias already exists. Please choose a different one.');
        }
        throw userError;
      }

      // Best-effort local persistence (does not block signup)
      try {
        void fetch('/api/store-user.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user: userData }),
        }).catch((e) => {
          console.warn('[AuthService.signUp] store-user.php failed', e);
          return null;
        });
      } catch {
        // ignore
      }

      // Best-effort signup email notification (does not block signup)
      try {
        void fetch('/api/notifications-dispatch.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: 'user_signup',
            user: {
              id: userData.id,
              email: userData.email,
              firstName: userData.first_name,
              lastName: userData.last_name,
              alias: (userData as any).alias || null,
            },
          }),
        }).catch((e) => {
          console.warn('[AuthService.signUp] notifications-dispatch.php failed', e);
          return null;
        });
      } catch {
        // ignore
      }

      return {
        user: this.transformUser(userData),
        session: authData.session,
      };
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  }

  // Sign in an existing user
  static async signIn(email: string, password: string) {
    try {
      ensureSupabaseConfigured();
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      if (!data.user) throw new Error('No user data returned');

      // Get user details from users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', data.user.id)
        .single();

      if (userError) throw userError;

      return {
        user: this.transformUser(userData),
        session: data.session,
      };
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  }

  // Sign out
  static async signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }

  // Get current user
  static async getCurrentUser(): Promise<AuthUser | null> {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (!authUser) return null;

      const { data: userData, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .single();

      if (error) throw error;
      return this.transformUser(userData);
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  // Check if user is admin
  static async isAdmin(): Promise<boolean> {
    try {
      const user = await this.getCurrentUser();
      return user?.isAdmin || false;
    } catch (error) {
      console.error('Check admin error:', error);
      return false;
    }
  }

  // Update user profile
  static async updateProfile(userId: string, updates: Partial<User>) {
    try {
      const { data, error } = await supabase
        .from('users')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return this.transformUser(data);
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  // Transform database user to AuthUser
  private static transformUser(user: User): AuthUser {
    return {
      id: user.id,
      email: user.email,
      firstName: user.first_name || undefined,
      lastName: user.last_name || undefined,
      isAdmin: user.is_admin,
      userStatusPreference: user.user_status_preference,
    };
  }

  // Listen to auth state changes
  static onAuthStateChange(callback: (user: AuthUser | null) => void) {
    return supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        const user = await this.getCurrentUser();
        callback(user);
      } else {
        callback(null);
      }
    });
  }
}
